import type { EconomyNaming, ProfileTheme, PublicWheelConfig, WheelId } from '@shared';
import { creditIconFor } from '../components/ui/currency';
import { creditWord } from '../lib/format';
import { ROUTES, type RouteDefinition, type RouteId } from './routes';

/**
 * A route's chrome — the label in the rail and the title in the top bar —
 * resolved against the live wheel configuration and the creator's own words.
 *
 * Two routes render a wheel, and a wheel's name belongs to the owner: it is
 * edited in the console and can change any afternoon. Hard-coding it in
 * `routes.ts` meant the menu said one thing while the same wheel, named from
 * its config on Home and in How To Play, said another. One name, one source.
 *
 * The same holds for the currency: "Earn credits" is wrong the moment a creator
 * calls hers something else, so the entries that name it are rebuilt here.
 *
 * Not a hook, so the navigation can resolve every entry from a single
 * `useGame()` call rather than calling one per item inside a loop.
 */

/** Route ids that render a wheel, and which wheel each one renders. */
const WHEEL_ROUTES: Partial<Record<RouteId, WheelId>> = {
  wheel: 'main',
  daily: 'daily',
  scratch: 'scratch',
};

/**
 * The part of a route's chrome that names the currency.
 *
 * Lives here rather than in `routes.ts` because it cannot be written down until
 * the creator's words are known; the literals over there are what these lines
 * come out as for a creator who has renamed nothing.
 */
function namedChrome(
  id: RouteId,
  naming: EconomyNaming,
  theme: ProfileTheme,
): Partial<RouteDefinition> {
  const money = creditWord(naming);
  switch (id) {
    case 'shop':
      // The icon travels with the words: a menu entry reading "Earn Moonpetals"
      // beside a stack of coins is the seam this whole layer exists to close.
      return { label: `Earn ${money}`, title: `Earn ${money}`, icon: creditIconFor(theme) };
    case 'wheel':
      return { subtitle: `Spend ${money}, win prizes` };
    default:
      return {};
  }
}

export function routeChrome(
  id: RouteId,
  wheelOf: (wheelId: WheelId) => PublicWheelConfig,
  naming: EconomyNaming,
  theme: ProfileTheme,
): RouteDefinition {
  const base = { ...ROUTES[id], ...namedChrome(id, naming, theme) };
  const wheelId = WHEEL_ROUTES[id];
  if (!wheelId) return base;

  const config = wheelOf(wheelId);
  // Falling back to the static text matters at boot: the first render happens
  // before `GET /api/game` lands, and an empty menu reads as a broken app.
  return {
    ...base,
    label: config.name || base.label,
    title: config.name || base.title,
    subtitle: config.description || base.subtitle,
  };
}
